import React, { forwardRef, ForwardRefRenderFunction, useState, useRef } from 'react'
import { DSInputType, Icon, Input } from 'components'
import * as S from './styles'
import { RegisterOptions } from 'react-hook-form'
import useOnClickOutside from 'commons/customHooks/useOnClickOutside'
import { theme } from 'styles/theme/hav-theme'

type Change = Exclude<RegisterOptions['onChange'], undefined>
export interface BatchSearchProps {
  placeHolder?: string
  label?: string
  height?: number
  required?: boolean
  disabled?: boolean
  onInputChange?: () => void
  value: string
  onChange: Change
  error?: string | null
  onClear?: () => void
}

const BatchSearchComponent: ForwardRefRenderFunction<
  HTMLInputElement,
  BatchSearchProps
> = (
  {
    placeHolder,
    label,
    height = 30,
    required,
    disabled = false,
    error,
    onClear = () => {
      return
    },
    value,
    onChange
  },
  ref
) => {
  const [open, setOpen] = useState(false)
  const [batch, setBatch] = useState([''])

  const toggleBatchArea = (inputValue = '') => {
    if (inputValue !== '') {
      setBatch(inputValue.toUpperCase().split(','))
    }
    setOpen(!open)
  }

  const batchRef = useRef<HTMLDivElement>(null)
  const inputAreaRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(
    batchRef,
    () => {
      setOpen(false)
    },
    inputAreaRef
  )

  const ParseTextarea = ({ valueTextarea = [] }: { valueTextarea: string[] }) => {
    const [text, setText] = React.useState<string>(valueTextarea.join('\n'))

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const valueText = e.target.value.toUpperCase()
      setText(valueText)
    }

    return (
      <div ref={batchRef}>
        <Input
          ref={ref}
          id="batch-area"
          data-testid="batch-search-component-textarea"
          name="batch-search"
          value={text}
          type="text"
          error={error}
          height={148}
          rows={5}
          onChange={handleChange}
          required
        />
        <S.WrapperButtons>
          <S.WrapIconStyled
            data-testid="batch-search-component-clear"
            type="button"
            disabled={batch?.length === 1 && batch[0] === '' && text === ''}
            onClick={() => {
              setText('')
              onClear()
              setBatch([''])
            }}
          >
            <Icon icon="clear-all" size="20" />
          </S.WrapIconStyled>
          <S.WrapIconStyled
            data-testid="batch-search-component-save"
            type="button"
            onClick={() => {
              onChange(
                text
                  .split('\n')
                  .filter((item) => item !== '')
                  .join(',')
              )
              setBatch([''])
              setOpen(false)
            }}
          >
            <Icon icon="save" size="20" />
          </S.WrapIconStyled>
        </S.WrapperButtons>
      </div>
    )
  }

  return (
    <S.Wrapper ref={inputAreaRef}>
      <Input
        data-testid="batch-search-component-input"
        label={label}
        design={DSInputType.MASTER_INPUT}
        disabled={disabled}
        required={required}
        value={value}
        height={height}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        InputProps={{
          placeholder: placeHolder,
          fullWidth: true,
          endAdornment: (
            <S.BatchIconStyled
              data-testid="batch-search-component-search-icon"
              disabled={disabled}
              onClick={() => toggleBatchArea(value)}
            >
              <Icon icon="batch" size="20" color={theme.colors.primary.main} />
            </S.BatchIconStyled>
          )
        }}
        id="batch-area-input"
        textUppercase
      />
      {!disabled && open && (
        <S.WrapperBatchArea
          data-testid="batch-search-component-batch-area"
          hasLabel={!!label}
          height={height}
        >
          <S.BatchArea>
            <ParseTextarea valueTextarea={batch} />
          </S.BatchArea>
        </S.WrapperBatchArea>
      )}
    </S.Wrapper>
  )
}

export const BatchSearch = forwardRef(BatchSearchComponent)
